/**
 * ResourceTable - Table display for resources with actions
 */
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FileText, 
  Eye, 
  Edit2, 
  Trash2, 
  Plus, 
  RefreshCw 
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  PROGRAMS,
  getProgramInfo, 
  getLibraryLabel, 
  formatFileSize 
} from "./ResourceConstants";

export function ResourceTable({ 
  token,
  resources, 
  loading, 
  searchQuery, 
  filterProgram, 
  filterLibrary,
  onEdit,
  onRefresh,
  onOpenUpload
}) {
  const handleDelete = async (resourceId, resourceTitle) => {
    if (!confirm(`Delete "${resourceTitle}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resource deleted");
      onRefresh?.();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete resource");
    }
  };

  const handlePreview = (resource) => {
    if (!resource.file_url) {
      toast.error("No file available for preview");
      return;
    }
    window.open(`${API}/resources/${resource.resource_id}/view`, '_blank');
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="p-12 text-center text-white/60">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading resources...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (resources.length === 0) {
    const hasFilters = searchQuery || filterProgram || filterLibrary;
    return (
      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="p-12 text-center text-white/60">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg mb-2">
              {hasFilters ? "No resources match your filters" : "No resources uploaded yet"}
            </p>
            <p className="text-sm text-white/40 mb-4">Upload your first PDF to get started</p>
            {!hasFilters && (
              <Button 
                className="btn-primary"
                onClick={onOpenUpload}
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Resource
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/60">Title</TableHead>
                <TableHead className="text-white/60">Program</TableHead>
                <TableHead className="text-white/60">Library</TableHead>
                <TableHead className="text-white/60">Type</TableHead>
                <TableHead className="text-white/60">Size</TableHead>
                <TableHead className="text-white/60">Created</TableHead>
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resources.map((resource) => {
                const programInfo = getProgramInfo(resource.program);
                return (
                  <TableRow 
                    key={resource.resource_id} 
                    className="border-white/10 hover:bg-white/5"
                    data-testid={`resource-row-${resource.resource_id}`}
                  >
                    <TableCell className="text-white max-w-xs">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{resource.title}</p>
                          {resource.description && (
                            <p className="text-white/50 text-sm line-clamp-1">{resource.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {programInfo ? (
                        <Badge 
                          variant="outline" 
                          className="border-white/20 text-white/80"
                          style={{ borderColor: `${programInfo.color}50`, color: programInfo.color }}
                        >
                          <programInfo.icon className="w-3 h-3 mr-1" />
                          {programInfo.label}
                        </Badge>
                      ) : (
                        <span className="text-white/40">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-white/70 text-sm">
                      {getLibraryLabel(resource.vault_chamber)}
                    </TableCell>
                    <TableCell className="text-white/70 uppercase text-xs">
                      {resource.resource_type || "pdf"}
                    </TableCell>
                    <TableCell className="text-white/70 text-sm">
                      {formatFileSize(resource.size)}
                    </TableCell>
                    <TableCell className="text-white/70 text-sm">
                      {new Date(resource.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-white/50 hover:text-primary hover:bg-primary/10"
                          onClick={() => handlePreview(resource)}
                          title="Preview"
                          data-testid={`preview-${resource.resource_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-white/50 hover:text-blue-400 hover:bg-blue-400/10"
                          onClick={() => onEdit(resource)}
                          title="Edit"
                          data-testid={`edit-${resource.resource_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-white/50 hover:text-red-400 hover:bg-red-400/10"
                          onClick={() => handleDelete(resource.resource_id, resource.title)}
                          title="Delete"
                          data-testid={`delete-${resource.resource_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
